/* istanbul ignore file */
export const API_ROUTES = {
  Dashboard: {
    statistics: (companyId: string) => `/dashboard/${companyId}/statistics`,
    activityLogs: (companyId: string) => `/dashboard/${companyId}/activity-logs`,
    specificationItemSources: (companyId: string) => `/dashboard/${companyId}/specification-item-sources`,
    lastActivities: (companyId: string) => `/dashboard/${companyId}/last-activities`,
    processedResources: (companyId: string) => `/dashboard/${companyId}/processed-resources`,
    contributionPerformance: (companyId: string) => `/dashboard/${companyId}/contribution-performance`,
    conflictResolutionPerformance: (companyId: string) => `/dashboard/${companyId}/conflict-resolution-performance`,
  },
  Workspaces: {
    Switch: "/auth/switch-workspace",
    Create: "/auth/create-workspace",
    Update: (companyId: string) => `/auth/update-workspace/${companyId}`,
    List: "/auth/workspaces",
    invite: "/workspace-invite",
    inviteAccept: (hash: string) => `/workspace-invite/accept/${hash}`,
  },
  Constants: {
    countries: "/countries",
    countriesId: (id: string) => `/countries/${id}`,
    languages: "/languages",
    languagesId: (id: string) => `/languages/${id}`,
    jobs: "/jobs",
    jobsId: (id: string) => `/jobs/${id}`,
    sectors: "/sectors",
    sectorsId: (id: string) => `/sectors/${id}`,
  },
  Session: {
    getSessions: "/user-sessions",
    terminate: (sessionId: string) => `/user-sessions/${sessionId}`,
    terminateAll: "/user-sessions/terminate-all",
  },
  Company: {
    company: "/company",
    update: (id: string) => `/company/${id}`,
    updateLogo: (id: string) => `/company/${id}/logo`,
    members: (companyId: string) => `/company/${companyId}/members`,
  },
  User: {
    user: "/user",
    get: (id: string) => `/user/${id}`,
    update: (id: string) => `/user/${id}`,
    delete: (id: string) => `/user/${id}/delete-account`,
    sendOtp: (id: string) => `/user/${id}/send-otp`,
    profilePicture: (id: string) => `/user/${id}/profile-picture`,
  },
  Auth: {
    OtpSend: "/auth/otp/send",
    SigninWithOauth: "/auth/signin-with-oauth",
    Register: "/auth/register",
    OtpVerify: "/auth/otp/verify",
    EmailActivation: "/auth/email-activation",
    RefreshToken: "/auth/refresh-token",
    SigninAnonymous: "/auth/signin-anonymous",
    Me: "/auth/me",
    SwitchWorkspace: "/auth/switch-workspace",
  },
  ProductCompare: {
    root: "/product-compare",
    detail: (specificationId: string) => `/product-compare/${specificationId}`,
  },
  chat: {
    root: "/ai-chat",
    getHistory: (specificationId: string, userId: string) =>
      `/ai-chat/${specificationId}/${userId}/history`,
    answerFollowUpQuestion: (id: string) =>
      `/ai-chat/answer-follow-up-question/${id}`,
    cancel: (specificationId: string, userId: string) =>
      `/ai-chat/cancel-stream/${specificationId}/${userId}`,
  },
  SpecPermission: {
    user: (userId: string) => `/specification-permission/user/${userId}`,
    delete: (permissionId: string) =>
      `/specification-permission/${permissionId}`,
  },
  Specification: {
    root: "/specification",
    list: (companyId: string) => `/specification/${companyId}`,
    itemAnswer: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/item-answers`,
    merge: (
      companyId: string,
      targetSpecificationId: string,
      sourceTemplateId: string
    ) =>
      `/specification/${companyId}/${targetSpecificationId}/merge/${sourceTemplateId}`,
    createWithAI: "/specification/generate-with-ai",
    rebasePrompt: "/specification/rebase-prompt",
    productCompare: "/specification/product-compare",
    productExplore: "/specification/product-explore",
    discoveryNotifications: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/notifications`,
    candidateProduct: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/candidate-products`,
    candidateProductBulkDelete: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/candidate-products/bulk-delete`,
    update: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}`,
    get: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}`,
    delete: (specificationId: string) => `/specification/${specificationId}`,
    restore: (specificationId: string) =>
      `/specification/${specificationId}/restore`,
    item: {
      root: "/specification/items",
      update: (itemId: string, companyId: string, specificationId: string) =>
        `/specification/${companyId}/${specificationId}/items/${itemId}`,
      delete: (itemId: string) => `/specification/items/${itemId}`,
      restore: (itemId: string) => `/specification/items/${itemId}/restore`,
      reorder: (
        companyId: string,
        specificationId: string,
        draggedId: string
      ) =>
        `/specification/${companyId}/${specificationId}/category-items/${draggedId}/reorder`,
      moveToCategory: (
        companyId: string,
        specificationId: string,
        targetId: string
      ) =>
        `/specification/${companyId}/${specificationId}/category-items/${targetId}/move-to-category`,
    },
    getProductExplore: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/explore`,
    explorePin: (
      companyId: string,
      specificationId: string,
      specificationExploreId: string
    ) =>
      `/specification/${companyId}/${specificationId}/explore/${specificationExploreId}`,
    pinAllExploreProducts: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/explore-bulk`,
    moveExploreProduct: "/specification/move",
    skip: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/skip`,
    files: `/specification/files`,
    convertToDocx: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/convert-to-docx`,
    convertToExcel: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/convert-to-excel`,
    hash: `/specification/hash`,
    baseCategories: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/base-categories`,
    clientRequirementsThreshold: "/specification/client-requirements-threshold",
    categorySelection: "/specification/category-selection",
    fetchCategorySpecificScore: "/specification/fetch-category-specific-score",
    generateTipItems: "/specification/generate-tip-items",
    checkManuelState: "/specification/check-manuel-state",
    productCategories: "/product-categories",
    share: {
      root: (specificationId: string) => `/share/permission/${specificationId}`,
      multiple: "/specification-invite/multiple",
      // activeCategories: (specificationId: string) =>
      //   `/share/active-categories/${specificationId}`
    },
    copy: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/copy`,
    category: {
      root: "/specification/categories",
      update: (
        categoryId: string,
        companyId: string,
        specificationId: string
      ) =>
        `/specification/${companyId}/${specificationId}/categories/${categoryId}`,
      delete: (categoryId: string) => `/specification/categories/${categoryId}`,
      get: (companyId: string, specificationId: string) =>
        `/specification/${companyId}/${specificationId}/categories`,
    },
    invite: {
      root: "/specification-invite",
      accept: (hash: string) => `/specification-invite/accept/${hash}`,
      detail: (hash: string) => `/specification-invite/detail/${hash}`,
      list: (specificationId: string) =>
        `/specification-invite/list/${specificationId}`,
      permission: (specificationId: string) =>
        `/specification-invite/permission/${specificationId}`,
      revoke: (specificationId: string) =>
        `/specification-invite/revoke/${specificationId}`,
      revokeInviteWidthInviteId: "/specification-invite/revoke",
      resend: "/specification-invite/resend",
    },
    template: {
      root: "/specification-template",
      get: (templateId: string) => `/specification-template/${templateId}`,
    },
    product: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/products`,
    productsAdd: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/products`,
    multipleDelete: "/specification/multiple-delete",
    downloadMultiple: "/specification/multiple-convert-to-docx",
  },
  SpecificationFolder: {
    root: "/specification-folders",
    get: (companyId: string) => `/specification-folders/${companyId}`,
    detail: (folderId: string) => `/specification-folders/detail/${folderId}`,
    create: "/specification-folders",
    update: (folderId: string) => `/specification-folders/${folderId}`,
    delete: (folderId: string) => `/specification-folders/${folderId}`,
    bulkDelete: "/specification-folders/bulk-delete",
    move: "/specification-folders/move",
    order: (companyId: string) => `/specification-folders/${companyId}/order`,
    list: (companyId: string) => `/specification-folders/${companyId}/list`,
  },
  Supplier: {
    register: (inviteId: string) => `/supplier/${inviteId}`,
    invite: `/specification-invite/supplier`,
    hash: (hash: string) => `/supplier/hash/${hash}`,
    categories: (hash: string) => `/supplier/hash/${hash}/categories`,
    sendAnswer: "/supplier/survey",
    complete: `/supplier/survey/complete`,
    getSupplierAndAIAnswers: (specificationId: string, productId: string) =>
      `/supplier/survey/${specificationId}/${productId}`,
  },
  Storage: {
    upload: "/storage/upload",
  },
  Region: {
    root: "/regions",
    update: (companyId: string, specificationId: string) =>
      `/specification/${companyId}/${specificationId}/region`,
  },
  Inbox: {
    root: "/inbox",
    getInbox: (userId: string) => `/inbox/${userId}`,
    markAsRead: (inboxId: string) => `/inbox/${inboxId}/mark-as-read`,
    activities: (specificationId: string) => `/activities/${specificationId}`,
    toggleFavorite: (inboxId: string) => `/inbox/${inboxId}/toggle-favorite`,
    delete: (inboxId: string) => `/inbox/${inboxId}`,
  },
  Report: {
    specificationDetail: (specificationId: string) =>
      `/report/specification-detail/${specificationId}`,
    specificationItemStats: (specificationId: string) =>
      `/report/specification-item-stats/${specificationId}`,
    specificationPinnedProducts: (specificationId: string) =>
      `/report/specification-pinned-products/${specificationId}`,
    specificationScoredProducts: (specificationId: string) =>
      `/report/specification-scored-products/${specificationId}`,
    specificationCandidateProducts: (specificationId: string) =>
      `/report/specification-candidate-products/${specificationId}`,
    specificationContributors: (specificationId: string) =>
      `/report/specification-contributors/${specificationId}`,
    supplierResponseDetails: (specificationId: string) =>
      `/report/supplier-response-details/${specificationId}`,
    supplierStatistics: (specificationId: string) =>
      `/report/specification-supplier-statistics/${specificationId}`,
    screenTimeStats: "/report/screen-time-stats",
    conflictingItems: (specificationId: string) =>
      `/report/conflicting-items/${specificationId}`,
    specificationProductStats: (specificationId: string) =>
      `/report/specification-product-stats/${specificationId}`,
    notFoundItems: (specificationId: string) =>
      `/report/not-found-items/${specificationId}`,
    specificationCreditStats: (specificationId: string) =>
      `/report/specification-credit-stats/${specificationId}`,
  },
  UserActivity: {
    log: "/user-activity-log",
  },
  SpecificationSupplierResponses: {
    list: (companyId: string) =>
      `/specification-supplier-responses/${companyId}`,
    products: (specificationId: string) =>
      `/specification-supplier-responses/${specificationId}/products`,
    itemComments: (itemId: string, productId: string) =>
      `/specification-supplier-responses/items/${itemId}/products/${productId}/comments`,
    supplierItemAnswers: (specificationId: string, productId: string) =>
      `/specification-supplier-responses/${specificationId}/supplier-item-answers?productId=${productId}`,
    conflicts: (productId: string, specificationId: string) =>
      `/specification-supplier-responses/products/${productId}/conflicts?specificationId=${specificationId}`,
    resolution: (itemId: string, productId: string, specificationId: string) =>
      `/specification-supplier-responses/items/${itemId}/products/${productId}/final-decision?specificationId=${specificationId}`,

    // Document answer processes
    documentAnswerProcesses: (specificationId: string) =>
      `/specification-supplier-responses/${specificationId}/document-answer/processes`,
    documentAnswerProcessDetail: (specificationId: string, manuelProcessId: string) =>
      `/specification-supplier-responses/${specificationId}/document-answer/processes/${manuelProcessId}`,
  },
  Service: {
    root: "/service",
    findBySpecificationId: (specificationId: string) =>
      `/service?specificationId=${specificationId}`,
  },
  SpecificationInviteService: {
    root: "/specification-invite/service",
  },
};
